import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlay,
    faStop,
    faPlus,
    faTrash,
    faCheck,
    faClock,
    faLink as faLinkIcon,
    faFileArrowDown,
    faFileInvoiceDollar,
    faFileLines,
    faUpload,
    faArrowUpRightFromSquare,
    faKey,
    faEye,
    faEyeSlash,
    faBug,
    faChevronDown,
    faChevronUp,
    faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import RichTextEditor from "../shared/RichTextEditor";
import RichTextView from "../shared/RichTextView";
import "./ProjectDetail.css";

const STATUS_OPTIONS = ["active", "on_hold", "completed", "cancelled"];

const FILE_CATEGORIES = {
    file: "File",
    audit: "Audit Report",
    deliverable: "Deliverable",
};

const LINK_CATEGORIES = {
    design: "Design",
    staging: "Staging",
    repo: "Repository",
    qc: "QC / Review",
    docs: "Docs",
    other: "Other",
};

const CRED_CATEGORIES = {
    hosting: "Hosting",
    domain: "Domain / DNS",
    ftp: "FTP / SFTP",
    database: "Database",
    cms: "CMS Admin",
    email: "Email",
    other: "Other",
};

const formatCAD = (n) =>
    n != null
        ? new Intl.NumberFormat("en-CA", {
              style: "currency",
              currency: "CAD",
          }).format(n)
        : "—";

const formatDuration = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
};

const formatClock = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const formatBytes = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1,
    );
    return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const plusOneMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
};

/* ---- Stage tracker ---- */
const StageTracker = ({ project, readOnly, onRefresh }) => {
    const [adding, setAdding] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const stages = project.stages || [];

    const cycleStatus = async (stage) => {
        if (readOnly) return;
        const next =
            stage.status === "pending"
                ? "in_progress"
                : stage.status === "in_progress"
                  ? "completed"
                  : "pending";
        await api.updateStage(stage.id, { status: next });
        onRefresh();
    };

    const addStage = async (e) => {
        e.preventDefault();
        if (!newTitle.trim()) return;
        await api.createStage(project.id, { title: newTitle.trim() });
        setNewTitle("");
        setAdding(false);
        onRefresh();
    };

    const removeStage = async (stage) => {
        if (!window.confirm(`Delete stage "${stage.title}"?`)) return;
        await api.deleteStage(stage.id);
        onRefresh();
    };

    const completed = stages.filter((s) => s.status === "completed").length;

    return (
        <div className="card pd-stages">
            <div className="pd-stages__header">
                <h3>Progress</h3>
                <span className="pd-stages__count">
                    {completed}/{stages.length} stages complete
                </span>
            </div>

            {stages.length === 0 ? (
                <p className="pd-empty">No stages defined yet.</p>
            ) : (
                <div className="pd-stages__track">
                    {stages.map((stage, i) => (
                        <div
                            key={stage.id}
                            className={`pd-stage pd-stage--${stage.status}`}
                        >
                            <button
                                type="button"
                                className="pd-stage__dot"
                                onClick={() => cycleStatus(stage)}
                                disabled={readOnly}
                                title={
                                    readOnly
                                        ? stage.status.replace("_", " ")
                                        : "Click to advance stage"
                                }
                            >
                                {stage.status === "completed" ? (
                                    <FontAwesomeIcon icon={faCheck} />
                                ) : (
                                    i + 1
                                )}
                            </button>
                            <div className="pd-stage__info">
                                <span className="pd-stage__title">
                                    {stage.title}
                                </span>
                                <span className="pd-stage__status">
                                    {stage.status.replace("_", " ")}
                                </span>
                            </div>
                            {!readOnly && (
                                <button
                                    type="button"
                                    className="pd-stage__remove"
                                    onClick={() => removeStage(stage)}
                                    title="Delete stage"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                            {i < stages.length - 1 && (
                                <span className="pd-stage__bar" />
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!readOnly &&
                (adding ? (
                    <form className="pd-stages__add" onSubmit={addStage}>
                        <input
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="Stage name…"
                            autoFocus
                        />
                        <button
                            type="submit"
                            className="btn btn--primary btn--sm"
                        >
                            Add
                        </button>
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setAdding(false)}
                        >
                            Cancel
                        </button>
                    </form>
                ) : (
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setAdding(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} /> Add stage
                    </button>
                ))}
        </div>
    );
};

/* ---- Time tracker ---- */
const TimeTracker = ({ project, readOnly, onRefresh }) => {
    const entries = useMemo(
        () => project.time_entries || [],
        [project.time_entries],
    );
    const running = entries.find((e) => e.is_running);
    const [note, setNote] = useState("");
    const [manualOpen, setManualOpen] = useState(false);
    const [manual, setManual] = useState({ minutes: "", note: "", date: "" });
    const [busy, setBusy] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const tickRef = useRef(null);

    // Live-ticking clock while a timer runs
    useEffect(() => {
        if (!running) {
            setElapsed(0);
            return undefined;
        }
        const startMs = new Date(running.started_at).getTime();
        const update = () =>
            setElapsed(Math.max(0, (Date.now() - startMs) / 1000));
        update();
        tickRef.current = setInterval(update, 1000);
        return () => clearInterval(tickRef.current);
    }, [running]);

    const baseSeconds = entries.reduce(
        (sum, e) => sum + (e.seconds != null ? e.seconds : 0),
        0,
    );
    const totalSeconds = baseSeconds + (running ? elapsed : 0);

    const start = async () => {
        setBusy(true);
        try {
            await api.startTimer(project.id, note ? { note } : {});
            setNote("");
            onRefresh();
        } finally {
            setBusy(false);
        }
    };

    const stop = async () => {
        setBusy(true);
        try {
            await api.stopTimer(project.id);
            onRefresh();
        } finally {
            setBusy(false);
        }
    };

    const addManual = async (e) => {
        e.preventDefault();
        if (!manual.minutes) return;
        setBusy(true);
        try {
            await api.addTimeEntry(project.id, {
                minutes: parseInt(manual.minutes, 10),
                note: manual.note || null,
                date: manual.date || null,
            });
            setManual({ minutes: "", note: "", date: "" });
            setManualOpen(false);
            onRefresh();
        } finally {
            setBusy(false);
        }
    };

    const removeEntry = async (entry) => {
        if (!window.confirm("Delete this time entry?")) return;
        await api.deleteTimeEntry(entry.id);
        onRefresh();
    };

    return (
        <div className="card pd-time">
            <div className="pd-time__header">
                <h3>
                    <FontAwesomeIcon icon={faClock} /> Time Tracking
                </h3>
                <span className="pd-time__total">
                    Total: <strong>{formatDuration(totalSeconds)}</strong>
                </span>
            </div>

            {!readOnly && (
                <div
                    className={`pd-time__clock ${running ? "pd-time__clock--running" : ""}`}
                >
                    <span className="pd-time__display">
                        {formatClock(running ? elapsed : 0)}
                    </span>
                    {running ? (
                        <>
                            {running.note && (
                                <span className="pd-time__note">
                                    {running.note}
                                </span>
                            )}
                            <button
                                className="btn btn--danger btn--sm"
                                onClick={stop}
                                disabled={busy}
                            >
                                <FontAwesomeIcon icon={faStop} /> Stop
                            </button>
                        </>
                    ) : (
                        <>
                            <input
                                className="pd-time__note-input"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="What are you working on?"
                            />
                            <button
                                className="btn btn--primary btn--sm"
                                onClick={start}
                                disabled={busy}
                            >
                                <FontAwesomeIcon icon={faPlay} /> Start
                            </button>
                        </>
                    )}
                </div>
            )}

            {!readOnly && (
                <div className="pd-time__manual">
                    {manualOpen ? (
                        <form
                            onSubmit={addManual}
                            className="pd-time__manual-form"
                        >
                            <input
                                type="number"
                                min="1"
                                max="1440"
                                placeholder="Minutes"
                                value={manual.minutes}
                                onChange={(e) =>
                                    setManual((p) => ({
                                        ...p,
                                        minutes: e.target.value,
                                    }))
                                }
                                required
                            />
                            <input
                                placeholder="Note (optional)"
                                value={manual.note}
                                onChange={(e) =>
                                    setManual((p) => ({
                                        ...p,
                                        note: e.target.value,
                                    }))
                                }
                            />
                            <input
                                type="date"
                                value={manual.date}
                                onChange={(e) =>
                                    setManual((p) => ({
                                        ...p,
                                        date: e.target.value,
                                    }))
                                }
                            />
                            <button
                                type="submit"
                                className="btn btn--primary btn--sm"
                                disabled={busy}
                            >
                                Log
                            </button>
                            <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setManualOpen(false)}
                            >
                                Cancel
                            </button>
                        </form>
                    ) : (
                        <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => setManualOpen(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Log time manually
                        </button>
                    )}
                </div>
            )}

            {entries.length > 0 && (
                <table className="dash-table pd-time__table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Note</th>
                            <th className="dash-table__right">Duration</th>
                            {!readOnly && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry) => (
                            <tr key={entry.id}>
                                <td className="dash-table__muted">
                                    {new Date(
                                        entry.started_at,
                                    ).toLocaleDateString("en-CA")}
                                </td>
                                <td>
                                    {entry.note || (
                                        <span className="dash-table__muted">
                                            —
                                        </span>
                                    )}
                                    {entry.is_running && (
                                        <span className="pd-time__running-tag">
                                            running
                                        </span>
                                    )}
                                </td>
                                <td className="dash-table__right dash-table__mono">
                                    {entry.is_running
                                        ? formatClock(elapsed)
                                        : formatDuration(entry.seconds || 0)}
                                </td>
                                {!readOnly && (
                                    <td className="dash-table__right">
                                        <button
                                            className="pd-icon-btn"
                                            onClick={() => removeEntry(entry)}
                                            title="Delete entry"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

/* ---- Quotes & change requests attached to this project ---- */
const ProjectQuotes = ({ project, readOnly }) => {
    const quotes = project.quotes || [];

    // Hide entirely for clients when there's nothing to show
    if (readOnly && quotes.length === 0) return null;

    return (
        <div className="card pd-quotes">
            <div className="pd-links__header">
                <h3>
                    <FontAwesomeIcon icon={faFileLines} /> Quotes & Change
                    Requests
                </h3>
                {!readOnly && (
                    <Link
                        className="btn btn--ghost btn--sm"
                        to={`/admin/quotes/new?client=${project.client_id}&project=${project.id}`}
                    >
                        <FontAwesomeIcon icon={faPlus} /> New Quote
                    </Link>
                )}
            </div>
            {quotes.length === 0 ? (
                <p className="pd-empty">
                    No quotes attached. Create one for scope additions or change
                    requests — when accepted it stays linked to this project.
                </p>
            ) : (
                <div className="pd-quotes__list">
                    {quotes.map((q) => (
                        <Link
                            key={q.id}
                            className="pd-quotes__item"
                            to={
                                readOnly
                                    ? `/dashboard/quotes/${q.id}`
                                    : `/admin/quotes/${q.id}`
                            }
                        >
                            <span className="pd-quotes__title">{q.title}</span>
                            <span className="pd-quotes__meta">
                                <span>{formatCAD(q.total)}</span>
                                <StatusBadge status={q.status} />
                            </span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ---- Links ---- */
const ProjectLinks = ({ project, readOnly, onRefresh }) => {
    const links = useMemo(() => project.links || [], [project.links]);
    const [editing, setEditing] = useState(false);
    const [rows, setRows] = useState(links);
    const [busy, setBusy] = useState(false);

    useEffect(() => setRows(links), [links]);

    const save = async () => {
        setBusy(true);
        try {
            const cleaned = rows.filter((r) => r.label.trim() && r.url.trim());
            await api.updateProject(project.id, { links: cleaned });
            setEditing(false);
            onRefresh();
        } finally {
            setBusy(false);
        }
    };

    if (readOnly && links.length === 0) return null;

    return (
        <div className="card pd-links">
            <div className="pd-links__header">
                <h3>
                    <FontAwesomeIcon icon={faLinkIcon} /> Links
                </h3>
                {!readOnly && !editing && (
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditing(true)}
                    >
                        Edit
                    </button>
                )}
            </div>

            {editing ? (
                <div className="pd-links__editor">
                    {rows.map((row, i) => (
                        <div key={i} className="pd-links__row">
                            <select
                                value={row.category || "other"}
                                onChange={(e) =>
                                    setRows((prev) =>
                                        prev.map((r, j) =>
                                            j === i
                                                ? {
                                                      ...r,
                                                      category: e.target.value,
                                                  }
                                                : r,
                                        ),
                                    )
                                }
                            >
                                {Object.entries(LINK_CATEGORIES).map(
                                    ([k, v]) => (
                                        <option key={k} value={k}>
                                            {v}
                                        </option>
                                    ),
                                )}
                            </select>
                            <input
                                placeholder="Label (e.g. Staging site)"
                                value={row.label}
                                onChange={(e) =>
                                    setRows((prev) =>
                                        prev.map((r, j) =>
                                            j === i
                                                ? {
                                                      ...r,
                                                      label: e.target.value,
                                                  }
                                                : r,
                                        ),
                                    )
                                }
                            />
                            <input
                                placeholder="https://…"
                                value={row.url}
                                onChange={(e) =>
                                    setRows((prev) =>
                                        prev.map((r, j) =>
                                            j === i
                                                ? { ...r, url: e.target.value }
                                                : r,
                                        ),
                                    )
                                }
                            />
                            <button
                                type="button"
                                className="pd-icon-btn"
                                onClick={() =>
                                    setRows((prev) =>
                                        prev.filter((_, j) => j !== i),
                                    )
                                }
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    ))}
                    <div className="pd-links__editor-actions">
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() =>
                                setRows((prev) => [
                                    ...prev,
                                    { label: "", url: "", category: "other" },
                                ])
                            }
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add link
                        </button>
                        <span style={{ flex: 1 }} />
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => {
                                setRows(links);
                                setEditing(false);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            onClick={save}
                            disabled={busy}
                        >
                            Save
                        </button>
                    </div>
                </div>
            ) : links.length === 0 ? (
                <p className="pd-empty">
                    No links yet. Add design files, staging site, repository, or
                    QC review URLs.
                </p>
            ) : (
                <div className="pd-links__list">
                    {links.map((link, i) => (
                        <a
                            key={i}
                            className="pd-links__chip"
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            {link.label}
                            <span className="pd-links__chip-cat">
                                {LINK_CATEGORIES[link.category] || "Link"}
                            </span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ---- Quick access credentials ---- */
const ProjectCredentials = ({ project, readOnly, onRefresh }) => {
    const creds = useMemo(
        () => project.credentials || [],
        [project.credentials],
    );
    const [editing, setEditing] = useState(false);
    const [rows, setRows] = useState(creds);
    const [busy, setBusy] = useState(false);
    const [revealed, setRevealed] = useState({});
    const [copied, setCopied] = useState(null);

    useEffect(() => setRows(creds), [creds]);

    const save = async () => {
        setBusy(true);
        try {
            const cleaned = rows.filter((r) => r.label && r.label.trim());
            await api.updateProject(project.id, { credentials: cleaned });
            setEditing(false);
            onRefresh();
        } finally {
            setBusy(false);
        }
    };

    const copy = (value, key) => {
        navigator.clipboard.writeText(value);
        setCopied(key);
        setTimeout(() => setCopied(null), 1500);
    };

    const setRow = (i, patch) =>
        setRows((prev) =>
            prev.map((r, j) => (j === i ? { ...r, ...patch } : r)),
        );

    if (readOnly && creds.length === 0) return null;

    return (
        <div className="card pd-creds">
            <div className="pd-links__header">
                <h3>
                    <FontAwesomeIcon icon={faKey} /> Access Details
                </h3>
                {!readOnly && !editing && (
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditing(true)}
                    >
                        Edit
                    </button>
                )}
            </div>

            {editing ? (
                <div className="pd-links__editor">
                    {rows.map((row, i) => (
                        <div key={i} className="pd-creds__edit-row">
                            <select
                                value={row.category || "other"}
                                onChange={(e) =>
                                    setRow(i, { category: e.target.value })
                                }
                            >
                                {Object.entries(CRED_CATEGORIES).map(
                                    ([k, v]) => (
                                        <option key={k} value={k}>
                                            {v}
                                        </option>
                                    ),
                                )}
                            </select>
                            <input
                                placeholder="Label (e.g. Bluehost cPanel)"
                                value={row.label || ""}
                                onChange={(e) =>
                                    setRow(i, { label: e.target.value })
                                }
                            />
                            <input
                                placeholder="URL (optional)"
                                value={row.url || ""}
                                onChange={(e) =>
                                    setRow(i, { url: e.target.value })
                                }
                            />
                            <input
                                placeholder="Host / Server (optional)"
                                value={row.host || ""}
                                onChange={(e) =>
                                    setRow(i, { host: e.target.value })
                                }
                            />
                            <input
                                placeholder="Username"
                                value={row.username || ""}
                                onChange={(e) =>
                                    setRow(i, { username: e.target.value })
                                }
                            />
                            <input
                                placeholder="Password"
                                value={row.password || ""}
                                onChange={(e) =>
                                    setRow(i, { password: e.target.value })
                                }
                            />
                            <input
                                placeholder="Notes (optional)"
                                value={row.notes || ""}
                                onChange={(e) =>
                                    setRow(i, { notes: e.target.value })
                                }
                            />
                            <button
                                type="button"
                                className="pd-icon-btn"
                                onClick={() =>
                                    setRows((prev) =>
                                        prev.filter((_, j) => j !== i),
                                    )
                                }
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    ))}
                    <div className="pd-links__editor-actions">
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() =>
                                setRows((prev) => [
                                    ...prev,
                                    {
                                        category: "hosting",
                                        label: "",
                                        url: "",
                                        host: "",
                                        username: "",
                                        password: "",
                                        notes: "",
                                    },
                                ])
                            }
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add access entry
                        </button>
                        <span style={{ flex: 1 }} />
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => {
                                setRows(creds);
                                setEditing(false);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            onClick={save}
                            disabled={busy}
                        >
                            Save
                        </button>
                    </div>
                </div>
            ) : creds.length === 0 ? (
                <p className="pd-empty">
                    No access details shared yet — hosting, domain/DNS, FTP,
                    database, or CMS admin credentials go here.
                </p>
            ) : (
                <div className="pd-creds__list">
                    {creds.map((cred, i) => (
                        <div key={i} className="pd-creds__item">
                            <div className="pd-creds__item-head">
                                <span className="pd-creds__item-title">
                                    <span className="pd-creds__cat">
                                        {CRED_CATEGORIES[cred.category] ||
                                            "Other"}
                                    </span>
                                    <strong>{cred.label}</strong>
                                </span>
                                {cred.url && (
                                    <a
                                        href={cred.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="pd-creds__open"
                                    >
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                        />{" "}
                                        Open
                                    </a>
                                )}
                            </div>
                            <div className="pd-creds__fields">
                                {cred.host && (
                                    <span className="pd-creds__field">
                                        <span className="pd-creds__field-label">
                                            Host
                                        </span>
                                        <code>{cred.host}</code>
                                        <button
                                            type="button"
                                            className="pd-icon-btn"
                                            title="Copy host"
                                            onClick={() =>
                                                copy(cred.host, `h${i}`)
                                            }
                                        >
                                            {copied === `h${i}` ? "✓" : "Copy"}
                                        </button>
                                    </span>
                                )}
                                {cred.username && (
                                    <span className="pd-creds__field">
                                        <span className="pd-creds__field-label">
                                            User
                                        </span>
                                        <code>{cred.username}</code>
                                        <button
                                            type="button"
                                            className="pd-icon-btn"
                                            title="Copy username"
                                            onClick={() =>
                                                copy(cred.username, `u${i}`)
                                            }
                                        >
                                            {copied === `u${i}` ? "✓" : "Copy"}
                                        </button>
                                    </span>
                                )}
                                {cred.password && (
                                    <span className="pd-creds__field">
                                        <span className="pd-creds__field-label">
                                            Pass
                                        </span>
                                        <code>
                                            {revealed[i]
                                                ? cred.password
                                                : "••••••••"}
                                        </code>
                                        <button
                                            type="button"
                                            className="pd-icon-btn"
                                            title={
                                                revealed[i] ? "Hide" : "Show"
                                            }
                                            onClick={() =>
                                                setRevealed((p) => ({
                                                    ...p,
                                                    [i]: !p[i],
                                                }))
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    revealed[i]
                                                        ? faEyeSlash
                                                        : faEye
                                                }
                                            />
                                        </button>
                                        <button
                                            type="button"
                                            className="pd-icon-btn"
                                            title="Copy password"
                                            onClick={() =>
                                                copy(cred.password, `p${i}`)
                                            }
                                        >
                                            {copied === `p${i}` ? "✓" : "Copy"}
                                        </button>
                                    </span>
                                )}
                            </div>
                            {cred.notes && (
                                <p className="pd-creds__notes">{cred.notes}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ---- Ybug QA feedback (aka "QC items") ---- */
const YbugFeedbackItem = ({ item, readOnly, busy, onToggleStatus }) => {
    const resolved = item.status === "resolved";
    return (
        <article
            className={`pd-ybug__item ${resolved ? "pd-ybug__item--resolved" : ""}`}
        >
            <div className="pd-ybug__item-top">
                <strong>{item.title || "Feedback"}</strong>
                <span className="pd-ybug__badges">
                    <StatusBadge status={item.status || "open"} />
                    {item.type && (
                        <span className="pd-ybug__badge">{item.type}</span>
                    )}
                    {item.priority && (
                        <span className="pd-ybug__badge pd-ybug__badge--priority">
                            {item.priority}
                        </span>
                    )}
                </span>
            </div>
            {item.comment && (
                <p className="pd-ybug__comment">{item.comment}</p>
            )}
            <div className="pd-ybug__item-meta">
                {item.url && (
                    <a href={item.url} target="_blank" rel="noreferrer">
                        {item.url}
                    </a>
                )}
                <span>
                    {item.reported_at
                        ? new Date(item.reported_at).toLocaleString("en-CA")
                        : ""}
                </span>
                {(item.reporter_name || item.reporter_email) && (
                    <span>
                        {[item.reporter_name, item.reporter_email]
                            .filter(Boolean)
                            .join(" · ")}
                    </span>
                )}
            </div>
            <div className="pd-ybug__item-actions">
                {item.screenshot_url && (
                    <a
                        className="btn btn--ghost btn--sm"
                        href={item.screenshot_url}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Screenshot
                    </a>
                )}
                {item.report_url && (
                    <a
                        className="btn btn--ghost btn--sm"
                        href={item.report_url}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Open in Ybug{" "}
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                )}
                {!readOnly && (
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onToggleStatus(item)}
                        disabled={busy}
                    >
                        <FontAwesomeIcon
                            icon={resolved ? faRotateLeft : faCheck}
                        />{" "}
                        {resolved ? "Reopen" : "Mark resolved"}
                    </button>
                )}
            </div>
        </article>
    );
};

const YbugFeedbackPanel = ({ project, readOnly, onRefresh }) => {
    const items = project.ybug_feedback || [];
    const [editing, setEditing] = useState(false);
    const [ybugId, setYbugId] = useState(project.ybug_project_id || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [collapsed, setCollapsed] = useState(false);
    const [showResolved, setShowResolved] = useState(false);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        setYbugId(project.ybug_project_id || "");
    }, [project.ybug_project_id]);

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            await api.updateProject(project.id, {
                ybug_project_id: ybugId.trim() || null,
            });
            setEditing(false);
            onRefresh();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (item) => {
        const nextStatus = item.status === "resolved" ? "open" : "resolved";
        setBusyId(item.id);
        try {
            await api.updateYbugFeedback(item.id, { status: nextStatus });
            onRefresh();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusyId(null);
        }
    };

    if (readOnly && items.length === 0 && !project.ybug_project_id) {
        return null;
    }

    const openItems = items.filter((item) => item.status !== "resolved");
    const resolvedItems = items.filter((item) => item.status === "resolved");

    return (
        <div className="card pd-ybug">
            <div className="pd-links__header">
                <button
                    type="button"
                    className="pd-ybug__collapse-toggle"
                    onClick={() => setCollapsed((v) => !v)}
                    title={collapsed ? "Expand" : "Collapse"}
                >
                    <FontAwesomeIcon
                        icon={collapsed ? faChevronDown : faChevronUp}
                    />
                    <h3>
                        <FontAwesomeIcon icon={faBug} /> QC / Staging Feedback
                        {items.length > 0 && (
                            <span className="pd-ybug__count">
                                {openItems.length} open
                            </span>
                        )}
                    </h3>
                </button>
                {!readOnly && !editing && (
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditing(true)}
                    >
                        {project.ybug_project_id ? "Edit link" : "Link Ybug"}
                    </button>
                )}
            </div>

            {!collapsed && (
                <>
                    {error && !editing && (
                        <div className="error-message">{error}</div>
                    )}

                    {editing && (
                        <div className="pd-ybug__link">
                            <p className="pd-empty">
                                Paste the Ybug project key from the widget
                                install code (or project settings). Reports
                                with that <code>projectId</code> will appear
                                here.
                            </p>
                            <div className="pd-links__row pd-ybug__row">
                                <input
                                    type="text"
                                    placeholder="Ybug project key"
                                    value={ybugId}
                                    onChange={(e) =>
                                        setYbugId(e.target.value)
                                    }
                                />
                                <button
                                    type="button"
                                    className="btn btn--primary btn--sm"
                                    onClick={save}
                                    disabled={saving}
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => {
                                        setEditing(false);
                                        setYbugId(
                                            project.ybug_project_id || "",
                                        );
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                            {error && (
                                <div className="error-message">{error}</div>
                            )}
                        </div>
                    )}

                    {!editing && project.ybug_project_id && !readOnly && (
                        <p className="pd-ybug__meta">
                            Linked to Ybug project{" "}
                            <code>{project.ybug_project_id}</code>
                        </p>
                    )}

                    {items.length === 0 ? (
                        <p className="pd-empty">
                            {project.ybug_project_id
                                ? "No feedback received yet. Submit a report from the staging widget to test."
                                : "Link a Ybug project to collect staging QA reports here."}
                        </p>
                    ) : (
                        <>
                            {openItems.length === 0 ? (
                                <p className="pd-empty">
                                    No open QC items — nice work.
                                </p>
                            ) : (
                                <div className="pd-ybug__list pd-ybug__list--scroll">
                                    {openItems.map((item) => (
                                        <YbugFeedbackItem
                                            key={item.id}
                                            item={item}
                                            readOnly={readOnly}
                                            busy={busyId === item.id}
                                            onToggleStatus={toggleStatus}
                                        />
                                    ))}
                                </div>
                            )}

                            {resolvedItems.length > 0 && (
                                <div className="pd-ybug__resolved">
                                    <button
                                        type="button"
                                        className="btn btn--ghost btn--sm"
                                        onClick={() =>
                                            setShowResolved((v) => !v)
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                showResolved
                                                    ? faChevronUp
                                                    : faChevronDown
                                            }
                                        />{" "}
                                        {showResolved ? "Hide" : "Show"}{" "}
                                        {resolvedItems.length} resolved
                                    </button>
                                    {showResolved && (
                                        <div className="pd-ybug__list pd-ybug__list--scroll">
                                            {resolvedItems.map((item) => (
                                                <YbugFeedbackItem
                                                    key={item.id}
                                                    item={item}
                                                    readOnly={readOnly}
                                                    busy={
                                                        busyId === item.id
                                                    }
                                                    onToggleStatus={
                                                        toggleStatus
                                                    }
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

/* ---- Files ---- */
const ProjectFiles = ({ project, readOnly, onRefresh }) => {
    const files = project.files || [];
    const [category, setCategory] = useState("file");
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setError("");
        try {
            await api.uploadProjectFile(project.id, file, category);
            onRefresh();
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = async (file) => {
        if (!window.confirm(`Delete "${file.original_name}"?`)) return;
        await api.deleteProjectFile(file.id);
        onRefresh();
    };

    if (readOnly && files.length === 0) return null;

    return (
        <div className="card pd-files">
            <div className="pd-files__header">
                <h3>
                    <FontAwesomeIcon icon={faFileLines} /> Files & Reports
                </h3>
                {!readOnly && (
                    <div className="pd-files__upload">
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            {Object.entries(FILE_CATEGORIES).map(([k, v]) => (
                                <option key={k} value={k}>
                                    {v}
                                </option>
                            ))}
                        </select>
                        <label className="btn btn--primary btn--sm">
                            <FontAwesomeIcon icon={faUpload} />{" "}
                            {uploading ? "Uploading…" : "Upload"}
                            <input
                                ref={inputRef}
                                type="file"
                                onChange={upload}
                                disabled={uploading}
                                hidden
                            />
                        </label>
                    </div>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}

            {files.length === 0 ? (
                <p className="pd-empty">No files shared yet.</p>
            ) : (
                <table className="dash-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Size</th>
                            <th>Uploaded</th>
                            <th className="dash-table__right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {files.map((file) => (
                            <tr key={file.id}>
                                <td>{file.original_name}</td>
                                <td>
                                    <span
                                        className={`pd-files__cat pd-files__cat--${file.category}`}
                                    >
                                        {FILE_CATEGORIES[file.category] ||
                                            file.category}
                                    </span>
                                </td>
                                <td className="dash-table__muted">
                                    {formatBytes(file.size)}
                                </td>
                                <td className="dash-table__muted">
                                    {new Date(
                                        file.created_at,
                                    ).toLocaleDateString("en-CA")}
                                </td>
                                <td className="dash-table__right">
                                    <button
                                        className="pd-icon-btn"
                                        title="Download"
                                        onClick={() =>
                                            api.downloadProjectFile(
                                                file.id,
                                                file.original_name,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faFileArrowDown}
                                        />
                                    </button>
                                    {!readOnly && (
                                        <button
                                            className="pd-icon-btn"
                                            title="Delete"
                                            onClick={() => remove(file)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

/* ---- Generate invoice modal ---- */
const GenerateInvoiceModal = ({ project, onClose }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        source: project.quote ? "quote" : "time",
        due_date: plusOneMonth(),
        hourly_rate: "",
        description: "",
        amount: "",
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { invoice } = await api.generateInvoice(project.id, {
                due_date: form.due_date,
                source: form.source,
                hourly_rate:
                    form.source === "time"
                        ? parseFloat(form.hourly_rate)
                        : undefined,
                description:
                    form.source === "custom" ? form.description : undefined,
                amount:
                    form.source === "custom"
                        ? parseFloat(form.amount)
                        : undefined,
            });
            navigate(`/admin/invoices/${invoice.id}`);
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    return (
        <div className="pd-modal" onClick={onClose}>
            <form
                className="pd-modal__box card"
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
            >
                <h3>
                    <FontAwesomeIcon icon={faFileInvoiceDollar} /> Generate
                    Invoice
                </h3>
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label>Bill from</label>
                    <select
                        value={form.source}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, source: e.target.value }))
                        }
                    >
                        {project.quote && (
                            <option value="quote">
                                Quote line items (
                                {formatCAD(project.quote.total)})
                            </option>
                        )}
                        <option value="time">Tracked time × hourly rate</option>
                        <option value="custom">Custom amount</option>
                    </select>
                </div>

                {form.source === "time" && (
                    <div className="form-group">
                        <label>Hourly rate (CAD)</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.hourly_rate}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    hourly_rate: e.target.value,
                                }))
                            }
                            required
                        />
                    </div>
                )}

                {form.source === "custom" && (
                    <>
                        <div className="form-group">
                            <label>Description</label>
                            <input
                                value={form.description}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        description: e.target.value,
                                    }))
                                }
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Amount (CAD)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.amount}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        amount: e.target.value,
                                    }))
                                }
                                required
                            />
                        </div>
                    </>
                )}

                <div className="form-group">
                    <label>Due date</label>
                    <input
                        type="date"
                        value={form.due_date}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, due_date: e.target.value }))
                        }
                        required
                    />
                </div>

                <div className="pd-modal__actions">
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn--primary btn--sm"
                        disabled={busy}
                    >
                        {busy ? "Creating…" : "Create Invoice"}
                    </button>
                </div>
            </form>
        </div>
    );
};

/* ---- Main component ---- */
const ProjectDetail = ({ readOnly = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [project, setProject] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(false);
    const [invoiceModal, setInvoiceModal] = useState(false);
    const [changingStatus, setChangingStatus] = useState(false);

    const load = React.useCallback(() => {
        api.project(id)
            .then(({ project: p }) => {
                setProject(p);
                setForm({
                    title: p.title,
                    description: p.description || "",
                    scope: p.scope || "",
                    status: p.status,
                    start_date: p.start_date || "",
                    end_date: p.end_date || "",
                    budget_cad: p.budget_cad ?? "",
                });
            })
            .catch(() => setError(t("common.error")))
            .finally(() => setLoading(false));
    }, [id, t]);

    useEffect(() => {
        load();
    }, [load]);

    const handleChange = (e) =>
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const { project: updated } = await api.updateProject(id, {
                title: form.title,
                description: form.description,
                scope: form.scope,
                status: form.status,
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                budget_cad:
                    form.budget_cad !== "" ? parseFloat(form.budget_cad) : null,
            });
            setProject(updated);
            setEditing(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (e) => {
        const next = e.target.value;
        if (
            next === "cancelled" &&
            !window.confirm(
                "Mark this project as cancelled? This won't delete any data.",
            )
        ) {
            return;
        }
        setChangingStatus(true);
        setError("");
        try {
            const { project: updated } = await api.updateProject(id, {
                status: next,
            });
            setProject(updated);
            setForm((p) => (p ? { ...p, status: next } : p));
        } catch (err) {
            setError(err.message);
        } finally {
            setChangingStatus(false);
        }
    };

    if (loading)
        return (
            <p style={{ color: "var(--color-text-muted)" }}>
                {t("common.loading")}
            </p>
        );
    if (!project)
        return (
            <p style={{ color: "var(--color-text-muted)" }}>
                Project not found.
            </p>
        );

    const invoices = project.invoices || [];

    return (
        <div className="project-detail">
            <div className="section-header">
                <div className="project-detail__title-row">
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() =>
                            navigate(
                                readOnly
                                    ? "/dashboard/projects"
                                    : "/admin/projects",
                            )
                        }
                    >
                        ← Back
                    </button>
                    <h2>{project.title}</h2>
                    {readOnly ? (
                        <StatusBadge status={project.status} />
                    ) : (
                        <label
                            className={`project-detail__status-select status-badge status-badge--${project.status}`}
                            title="Change project status"
                        >
                            <select
                                value={project.status}
                                onChange={handleStatusChange}
                                disabled={changingStatus}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s.replace("_", " ")}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
                <div className="project-detail__header-actions">
                    {!readOnly && (
                        <button
                            className="btn btn--primary btn--sm"
                            onClick={() => setInvoiceModal(true)}
                        >
                            <FontAwesomeIcon icon={faFileInvoiceDollar} />{" "}
                            Generate Invoice
                        </button>
                    )}
                    {!readOnly && !editing && (
                        <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => setEditing(true)}
                        >
                            Edit
                        </button>
                    )}
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Workflow breadcrumb: Quote -> Project -> Invoice */}
            <div className="pd-workflow card">
                <div
                    className={`pd-workflow__step ${project.quote ? "pd-workflow__step--done" : "pd-workflow__step--skipped"}`}
                >
                    <span className="pd-workflow__label">Quote</span>
                    {project.quote ? (
                        <Link
                            to={
                                readOnly
                                    ? `/dashboard/quotes/${project.quote.id}`
                                    : `/admin/quotes/${project.quote.id}`
                            }
                        >
                            {project.quote.title} —{" "}
                            {formatCAD(project.quote.total)}
                        </Link>
                    ) : (
                        <span className="pd-workflow__none">
                            No linked quote
                        </span>
                    )}
                </div>
                <span className="pd-workflow__arrow">→</span>
                <div className="pd-workflow__step pd-workflow__step--current">
                    <span className="pd-workflow__label">Project</span>
                    <span>{project.title}</span>
                </div>
                <span className="pd-workflow__arrow">→</span>
                <div
                    className={`pd-workflow__step ${invoices.length ? "pd-workflow__step--done" : ""}`}
                >
                    <span className="pd-workflow__label">
                        Invoice{invoices.length > 1 ? "s" : ""}
                    </span>
                    {invoices.length === 0 ? (
                        <span className="pd-workflow__none">
                            Not invoiced yet
                        </span>
                    ) : (
                        <span className="pd-workflow__invoices">
                            {invoices.map((inv) => (
                                <Link
                                    key={inv.id}
                                    to={
                                        readOnly
                                            ? `/dashboard/invoices/${inv.id}`
                                            : `/admin/invoices/${inv.id}`
                                    }
                                >
                                    {inv.invoice_number}
                                </Link>
                            ))}
                        </span>
                    )}
                </div>
            </div>

            {!readOnly && project.client && (
                <p className="project-detail__client">
                    Client:{" "}
                    <Link to={`/admin/clients/${project.client.id}`}>
                        <strong>
                            {project.client.name}
                            {project.client.company
                                ? ` · ${project.client.company}`
                                : ""}
                        </strong>
                    </Link>
                </p>
            )}

            <StageTracker
                project={project}
                readOnly={readOnly}
                onRefresh={load}
            />

            <ProjectQuotes project={project} readOnly={readOnly} />

            {editing && form ? (
                <form
                    className="project-detail__form card"
                    onSubmit={handleSave}
                >
                    <div className="project-detail__form-grid">
                        <div className="form-group">
                            <label>Title *</label>
                            <input
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s.replace("_", " ")}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Budget (CAD)</label>
                            <input
                                type="number"
                                name="budget_cad"
                                value={form.budget_cad}
                                onChange={handleChange}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div className="form-group">
                            <label>Start Date</label>
                            <input
                                type="date"
                                name="start_date"
                                value={form.start_date}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>End Date</label>
                            <input
                                type="date"
                                name="end_date"
                                value={form.end_date}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <RichTextEditor
                            value={form.description}
                            onChange={(html) =>
                                setForm((p) => ({ ...p, description: html }))
                            }
                            placeholder="Project overview…"
                        />
                    </div>
                    <div className="form-group">
                        <label>Scope</label>
                        <RichTextEditor
                            value={form.scope}
                            onChange={(html) =>
                                setForm((p) => ({ ...p, scope: html }))
                            }
                            placeholder="Deliverables, milestones, requirements…"
                        />
                    </div>
                    <div className="project-detail__form-actions">
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setEditing(false)}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            type="submit"
                            className="btn btn--primary btn--sm"
                            disabled={saving}
                        >
                            {saving ? "Saving…" : t("common.save")}
                        </button>
                    </div>
                </form>
            ) : (
                <>
                    <div className="project-detail__meta card">
                        <div className="project-detail__meta-grid">
                            <div>
                                <span className="project-detail__label">
                                    Budget
                                </span>
                                <span>{formatCAD(project.budget_cad)}</span>
                            </div>
                            <div>
                                <span className="project-detail__label">
                                    Start
                                </span>
                                <span>{project.start_date || "—"}</span>
                            </div>
                            <div>
                                <span className="project-detail__label">
                                    End
                                </span>
                                <span>{project.end_date || "—"}</span>
                            </div>
                            <div>
                                <span className="project-detail__label">
                                    Time Logged
                                </span>
                                <span>
                                    {formatDuration(
                                        project.total_tracked_seconds || 0,
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    {(project.description || !readOnly) && (
                        <div className="project-detail__section card">
                            <h3>Description</h3>
                            <RichTextView
                                html={project.description}
                                emptyText="No description yet."
                            />
                        </div>
                    )}

                    {(project.scope || !readOnly) && (
                        <div className="project-detail__section card">
                            <h3>Scope</h3>
                            <RichTextView
                                html={project.scope}
                                emptyText="No scope defined yet."
                            />
                        </div>
                    )}
                </>
            )}

            <ProjectLinks
                project={project}
                readOnly={readOnly}
                onRefresh={load}
            />

            <ProjectCredentials
                project={project}
                readOnly={readOnly}
                onRefresh={load}
            />

            <YbugFeedbackPanel
                project={project}
                readOnly={readOnly}
                onRefresh={load}
            />

            <ProjectFiles
                project={project}
                readOnly={readOnly}
                onRefresh={load}
            />

            <TimeTracker
                project={project}
                readOnly={readOnly}
                onRefresh={load}
            />

            {invoiceModal && (
                <GenerateInvoiceModal
                    project={project}
                    onClose={() => setInvoiceModal(false)}
                />
            )}
        </div>
    );
};

export default ProjectDetail;
